/**
 * `<DataTable/>` — sleek, dense table primitive.
 *
 * Per docs/launch/POC_UX_IMPROVEMENT_SPEC.md §3.2 — adopts v2's tight
 * "operations console" feel: sticky header, hairline borders, zebra
 * rows, `text-xs` body, `tabular-nums` on numeric cells, right-aligned
 * numerics. Drop into the existing Card chrome (header + content); the
 * primitive owns the table itself, not the surrounding panel.
 *
 * Usage:
 *
 *   <DataTable>
 *     <DataTableHead>
 *       <DataTableRow>
 *         <DataTableHeader>Name</DataTableHeader>
 *         <DataTableHeader align="right">Hours</DataTableHeader>
 *       </DataTableRow>
 *     </DataTableHead>
 *     <DataTableBody>
 *       {rows.map((r) => (
 *         <DataTableRow key={r.id}>
 *           <DataTableCell>{r.name}</DataTableCell>
 *           <DataTableCell align="right" numeric>{r.hours}</DataTableCell>
 *         </DataTableRow>
 *       ))}
 *     </DataTableBody>
 *   </DataTable>
 *
 * Cells default to `text-xs` for density. Pass `numeric` on numeric
 * cells to opt in to `tabular-nums` (so column digits line up). The
 * convention across the codebase: right-align money + counts, left-
 * align everything else.
 */

import {
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  forwardRef,
} from "react";

import { cn } from "../../lib/cn";

interface DataTableProps extends HTMLAttributes<HTMLTableElement> {
  /** Wrap in an overflow-x-auto div for narrow viewports. Default true. */
  scroll?: boolean;
}

/**
 * Root `<table>` with the v2 density rhythm. Wraps in an
 * `overflow-x-auto` div by default so narrow viewports horizontal-
 * scroll instead of clipping.
 */
export const DataTable = forwardRef<HTMLTableElement, DataTableProps>(
  ({ className, scroll = true, children, ...props }, ref) => {
    const table = (
      <table ref={ref} className={cn("w-full text-xs", className)} {...props}>
        {children}
      </table>
    );
    if (!scroll) return table;
    return <div className="overflow-x-auto">{table}</div>;
  },
);
DataTable.displayName = "DataTable";

/**
 * `<thead>` with the sticky-header v2 pattern. The header sticks to
 * `top-0` of its scroll parent and paints `bg-bg-elevated` so rows
 * scrolling underneath don't leak through.
 */
export const DataTableHead = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-10 bg-bg-elevated text-left uppercase tracking-[0.12em] text-fg-faint",
      className,
    )}
    {...props}
  />
));
DataTableHead.displayName = "DataTableHead";

export const DataTableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("text-fg-muted", className)} {...props} />
));
DataTableBody.displayName = "DataTableBody";

interface DataTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Disable zebra striping on this row. */
  noZebra?: boolean;
}

/**
 * Zebra-striped rows: every-other body row paints `bg-bg-subtle/50`.
 * Header rows opt out (the head already paints its own background).
 * The hairline `border-border/60` between rows is the v2 lift — no
 * shadow, just a thin divider.
 */
export const DataTableRow = forwardRef<HTMLTableRowElement, DataTableRowProps>(
  ({ className, noZebra, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-border/60 last:border-0",
        noZebra ? null : "even:bg-bg-subtle/50",
        className,
      )}
      {...props}
    />
  ),
);
DataTableRow.displayName = "DataTableRow";

interface DataTableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
}

export const DataTableHeader = forwardRef<HTMLTableCellElement, DataTableHeaderCellProps>(
  ({ className, align = "left", ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "px-3 py-2 font-medium",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className,
      )}
      {...props}
    />
  ),
);
DataTableHeader.displayName = "DataTableHeader";

interface DataTableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
  /** Apply `tabular-nums` so numeric columns line up. */
  numeric?: boolean;
}

export const DataTableCell = forwardRef<HTMLTableCellElement, DataTableCellProps>(
  ({ className, align = "left", numeric, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "px-3 py-1.5",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        numeric ? "tabular-nums" : null,
        className,
      )}
      {...props}
    />
  ),
);
DataTableCell.displayName = "DataTableCell";
